import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonCard, IonCardContent, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";


const numbers = Array.from({ length: 40 }, (_, i) => (i + 11).toString()); // Numbers from 11 to 50

const BeforeAfterBetweenNumbers: React.FC = () => {
    const [question, setQuestion] = useState("");
    const [correctAnswer, setCorrectAnswer] = useState(" ");
    const [options, setOptions] = useState<string[]>([]);
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Before, After and Between Number | Mama’s Guidance";
    }, []);

    const generateQuestion = () => {
        const randomIndex = Math.floor(Math.random() * (numbers.length - 2)) + 1;
        const type = Math.floor(Math.random() * 3); // 0 = Before, 1 = After, 2 = Between
        let newQuestion = "";
        let correct = "";

        if (type === 0) {
            newQuestion = `What number comes before '${numbers[randomIndex]}'?`;
            correct = numbers[randomIndex - 1];
        } else if (type === 1) {
            newQuestion = `What number comes after '${numbers[randomIndex]}'?`;
            correct = numbers[randomIndex + 1];
        } else {
            newQuestion = `Which number is between '${numbers[randomIndex - 1]}' and '${numbers[randomIndex + 1]}'?`;
            correct = numbers[randomIndex];
        }

        setQuestion(newQuestion);
        setCorrectAnswer(correct);

        const optionsArray = shuffleArray([
            correct,
            numbers[(randomIndex + 3) % numbers.length],
            numbers[(randomIndex + 5) % numbers.length],
            numbers[(randomIndex + 7) % numbers.length],
        ]);
        setOptions(optionsArray);
    };

    const shuffleArray = (array: string[]) => {
        return array.sort(() => Math.random() - 0.5);
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setMessage("❌ Try Again!");
        }
    };

    return (
        <>

            <Helmet>
                <meta name="description" content='Teach LKG kids the concepts of before, after, and between numbers with engaging activities on Mama’s Guidance—designed to develop number sense and sequencing skills through fun, interactive learning tools.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord heavyLight matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Before, After and Between Number</h1>

                                    <p>
                                        Understanding number sequence is a crucial part of early math learning. This worksheet focuses on the concepts of <strong>before</strong>, <strong>after</strong>, and <strong>between</strong>, helping LKG students recognize number order and improve logical thinking through playful and visual exercises.
                                    </p>

                                    <h2>🔢 What Do Before, After, and Between Mean?</h2>
                                    <p>
                                        These terms help children understand the order of numbers:
                                    </p>
                                    <ul>
                                        <li><strong>Before:</strong> A number that comes earlier (<strong>e.g., before 5 is 4</strong>)</li>
                                        <li><strong>After:</strong> A number that comes later (<strong>e.g., after 6 is 7</strong>)</li>
                                        <li><strong>Between:</strong> A number that is in the middle of two numbers (<strong>e.g., between 2 and 4 is 3</strong>)</li>
                                    </ul>

                                    <h2>🧩 What’s Included in the Worksheet?</h2>
                                    <ul>
                                        <li>Number line activities for 1 to 20</li>
                                        <li>Fill in the blanks: What comes <strong>before/after/between</strong>?</li>
                                        <li>Color the correct number among three options</li>
                                        <li>Illustrated tasks using <strong>number objects and fun visuals</strong></li>
                                        <li>Practice with both <strong>number recognition and sequence logic</strong></li>
                                    </ul>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify numbers that come <strong>before</strong> and <strong>after</strong> a given number</li>
                                        <li>Recognize the number that comes <strong>between</strong> two numbers</li>
                                        <li>Strengthen <strong>early sequencing and number sense</strong></li>
                                        <li>Develop <strong>logical reasoning and observation skills</strong></li>
                                    </ul>

                                    <h2>👩‍🏫 How to Teach the Concept</h2>
                                    <ul>
                                        <li>Use a number line or flashcards to explain <strong>before, after, and between</strong>.</li>
                                        <li>Practice with real-life examples like toys: <strong>“What’s between the car and the teddy?”</strong></li>
                                        <li>Ask questions like <strong>“What comes after 3?”</strong> or <strong>“What number is between 5 and 7?”</strong></li>
                                        <li>Reinforce daily during play or meal routines using cutlery or books.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Long - Short</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Big - Small</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Full - Empty</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why Is This Concept Important?</h2>
                                    <p>
                                        Mastering <strong>before, after, and between</strong> helps in understanding number order, direction, and basic math operations. It also supports future learning in <strong>addition, subtraction, and story sequencing</strong>.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Before, After and Between Number</h1>

                                <p>
                                    Understanding number sequence is a crucial part of early math learning. This worksheet focuses on the concepts of <strong>before</strong>, <strong>after</strong>, and <strong>between</strong>, helping LKG students recognize number order and improve logical thinking through playful and visual exercises.
                                </p>

                                <h2>🔢 What Do Before, After, and Between Mean?</h2>
                                <p>
                                    These terms help children understand the order of numbers:
                                </p>
                                <ul>
                                    <li><strong>Before:</strong> A number that comes earlier (<strong>e.g., before 5 is 4</strong>)</li>
                                    <li><strong>After:</strong> A number that comes later (<strong>e.g., after 6 is 7</strong>)</li>
                                    <li><strong>Between:</strong> A number that is in the middle of two numbers (<strong>e.g., between 2 and 4 is 3</strong>)</li>
                                </ul>

                                <h2>🧩 What’s Included in the Worksheet?</h2>
                                <ul>
                                    <li>Number line activities for 1 to 20</li>
                                    <li>Fill in the blanks: What comes <strong>before/after/between</strong>?</li>
                                    <li>Color the correct number among three options</li>
                                    <li>Illustrated tasks using <strong>number objects and fun visuals</strong></li>
                                    <li>Practice with both <strong>number recognition and sequence logic</strong></li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify numbers that come <strong>before</strong> and <strong>after</strong> a given number</li>
                                    <li>Recognize the number that comes <strong>between</strong> two numbers</li>
                                    <li>Strengthen <strong>early sequencing and number sense</strong></li>
                                    <li>Develop <strong>logical reasoning and observation skills</strong></li>
                                </ul>

                                <h2>👩‍🏫 How to Teach the Concept</h2>
                                <ul>
                                    <li>Use a number line or flashcards to explain <strong>before, after, and between</strong>.</li>
                                    <li>Practice with real-life examples like toys: <strong>“What’s between the car and the teddy?”</strong></li>
                                    <li>Ask questions like <strong>“What comes after 3?”</strong> or <strong>“What number is between 5 and 7?”</strong></li>
                                    <li>Reinforce daily during play or meal routines using cutlery or books.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/long-short"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Long - Short</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Big - Small</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Full - Empty</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why Is This Concept Important?</h2>
                                <p>
                                    Mastering <strong>before, after, and between</strong> helps in understanding number order, direction, and basic math operations. It also supports future learning in <strong>addition, subtraction, and story sequencing</strong>.
                                </p>
                            </article>
                            <h2>{question}</h2>
                            <div className="mt-4 mb-5" style={{ display: 'flex', gap: '15px' }}>
                                {options.map((option) => (
                                    <div style={{ width: 'auto', padding: '15px', fontSize: '20px' }} className=" background-blue" key={option} onClick={() => checkAnswer(option)}>
                                        {option}
                                    </div>
                                ))}
                            </div>
                            <p>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default BeforeAfterBetweenNumbers;
